import { detectWebCapabilities, type WebCapabilityEnvironment } from '../capabilities.js'
import type {
  BackupDestinationProvider,
  ContactPickerProvider,
  DisabledReason,
  FilePickerProvider,
  OnDeviceAIProvider,
  PlatformCapabilities,
  PurchaseProvider,
  ScanProvider,
  ShareProvider,
} from '../types.js'
import { createWebFilePickerProvider, type WebFilePickerEnvironment } from './files.js'
import {
  createUnavailablePurchaseProvider,
  createWebBackupDestinationProvider,
  createWebContactPickerProvider,
  createWebOnDeviceAIProvider,
  createWebScanProvider,
  type WebBackupEnvironment,
  type WebContactPickerEnvironment,
  type WebScanEnvironment,
} from './providers.js'
import { createWebShareProvider, type WebShareEnvironment } from './share.js'

/**
 * Web composition root — S0.7 exit criterion: the app (S0.8) obtains every
 * provider and the §10.1 capability object from one place.
 */

export interface WebPlatformEnvironment
  extends
    WebCapabilityEnvironment,
    WebShareEnvironment,
    WebFilePickerEnvironment,
    WebScanEnvironment,
    WebContactPickerEnvironment,
    WebBackupEnvironment {}

export interface WebPlatformProviders {
  readonly share: ShareProvider
  readonly file_picker: FilePickerProvider
  readonly scan: ScanProvider
  readonly contacts: ContactPickerProvider
  readonly purchase: PurchaseProvider
  readonly backup: BackupDestinationProvider
  readonly local_ai: OnDeviceAIProvider
}

export interface WebPlatform {
  readonly capabilities: PlatformCapabilities
  readonly providers: WebPlatformProviders
  /** Disabled optional controls with their working fallback (PRD §10.1). */
  readonly disabled_reasons: readonly DisabledReason[]
}

export function createWebPlatform(env: WebPlatformEnvironment = {}): WebPlatform {
  const capabilities = detectWebCapabilities(env)
  const share = createWebShareProvider(env)
  const filePicker = createWebFilePickerProvider(env)
  const scan = createWebScanProvider(env)
  const contacts = createWebContactPickerProvider(capabilities.contact_picker ? env : {})
  const purchase = createUnavailablePurchaseProvider()
  const backup = createWebBackupDestinationProvider({
    adapters: capabilities.backup_adapters,
    ...(env.choose_destination === undefined ? {} : { choose_destination: env.choose_destination }),
    ...(env.write_file === undefined ? {} : { write_file: env.write_file }),
    ...(env.download === undefined ? {} : { download: env.download }),
  })
  const localAi = createWebOnDeviceAIProvider(capabilities.local_ai_adapter)

  const disabledReasons: DisabledReason[] = []
  if (!capabilities.share_url && !capabilities.share_files) {
    disabledReasons.push({ code: 'SHARE_UNAVAILABLE', fallback: 'copy_link_or_qr' })
  }
  if (env.show_open_file_picker === undefined && env.has_dom === false) {
    disabledReasons.push({ code: 'FILE_PICKER_UNAVAILABLE', fallback: 'drag_and_drop_or_download' })
  }
  if (!scan.available) {
    disabledReasons.push({ code: 'CAMERA_UNAVAILABLE', fallback: 'import_file_instead' })
  }
  if (!contacts.available) {
    disabledReasons.push({
      code: 'CONTACT_PICKER_UNAVAILABLE',
      fallback: 'type_recipient_manually',
    })
  }
  if (purchase.provider_id === 'unavailable') {
    disabledReasons.push({ code: 'PURCHASE_UNAVAILABLE', fallback: 'local_and_manual_paths' })
  }
  if (!localAi.available) {
    disabledReasons.push({ code: 'LOCAL_AI_UNAVAILABLE', fallback: 'deterministic_prepare' })
  }

  return {
    capabilities,
    providers: {
      share,
      file_picker: filePicker,
      scan,
      contacts,
      purchase,
      backup,
      local_ai: localAi,
    },
    disabled_reasons: disabledReasons,
  }
}
